import { get as val } from 'object-path'
import * as immu from 'object-path-immutable'

// Names export
export function provider( xeon ) {

  // ---------------------------------
  // Create object with id drug.id
  // ---------------------------------
  const rules = Object.values( xeon.rules ).reduce( ( acc, cur ) => {
    if ( cur.group == "kinetic" ) {
      acc[ cur.drug ] = cur
    }
    return acc
  }, {} )

  return {

    // ----------------------------------------------
    // Give kinetic a score between 0 - 100
    // ----------------------------------------------
    score: function ( scale = 3 ) {
      const score = this.abstract().reduce( ( acc, cur ) => {
        if ( !cur.netef || cur.status == 'failure_invalid' ) return acc
        var value = 0
        const major = 4
        const moder = 2
        // Use the net effect for scoring: note that in this
        // way there is no difference whether the exposure is changed
        // 3 or 20 times
        if ( cur.status == 'failure_uncle' ) {
          value = moder
        } else if ( cur.netef == "++" || cur.netef == "--" ) {
          value = major
        } else if ( cur.netef == "+" || cur.netef == "-" ) {
          value = moder
        }
        // Multiply the score by 2 or 2.5 if NTI, depending on monitor
        if ( cur.nti == true && cur.monitor == true ) {
          value *= 2
        } else if ( cur.nti == true && cur.monitor == false ) {
          value *= 2.5
        }
        return acc + value
      }, 0 )
      return Math.min( 100, parseInt( score * scale ) )
    },

    // ----------------------------------------------
    // Good working list with all the required info
    // ----------------------------------------------
    list: function ( lang = "de" ) {
      return Object.values( xeon.drugs ).map( drug => {
        return { id: drug.id, name: drug.name[ lang ], rule: rules[ drug.id ] }
      } )
    },

    // -----------------------------------------------
    // Prepare stuff for colorful table in abstract
    // -----------------------------------------------
    abstract: function ( lang ) {
      return this.list( lang ).map( item => {
        const rule = item.rule || {}
        rule.param = rule.param || []
        const predict = rule.predict
        const interva = predict && predict.auc_interval && predict.auc_interval.length
        var value = predict ? predict.auc_ratio : null
        var range = interva ? predict.auc_interval : null
        const trend = predict ? predict.auc_trend : null
        const netef = predict ? predict.net_effect : null
        const status = predict ? predict.status : null
        if ( status == "failure_uncle" ) {
          value = 1
          range = [ 0.3, 3 ]
        } else if ( status != "success_quant" && netef ) {
          if ( netef == "≈" ) {
            value = 1.375
            range = [ 0.67, 1.5 ]
          } else if ( netef == "+" ) {
            value = 2.25
            range = [ 1.5, 3 ]
          } else if ( netef == "++" ) {
            value = 6.5
            range = [ 3, 10 ]
          } else if ( netef == "-≈" ) {
            value = 0.735
            range = [ 0.67, 0.8 ]
          } else if ( netef == "-" ) {
            value = 0.51
            range = [ 0.33, 0.67 ]
          } else if ( netef == "--" ) {
            value = 0.165
            range = [ 0.01, 0.33 ]
          } else {
            value = 1
            range = [ 0.8, 1.25 ]
          }
        }
        return {
          id: item.id,
          name: item.name,
          value: predict ? predict.auc_ratio : null,
          range: interva ? predict.auc_interval : null,
          trend: predict ? predict.auc_trend : null,
          netef: netef,
          value: value,
          range: range,
          status: status,
          // Needed to ugprade score for nti
          nti: rule.param.some( line => line.includes( "nti true" ) ),
          monitor: rule.param.some( line => line.includes( "monitor " ) )
        }
      } )
    },

    // ---------------------------------------
    // Find factors relevant for exposure for
    // - createPredict
    // - createTable
    // ---------------------------------------
    factors: function () {

      const items = this.list()
      const factors = {}

      // in items
      for ( const item of Object.values( items ) ) {
        const status = val( item, "rule.predict.status" ) || ""
        // Keep the factors only when can predict AUC change
        if ( status !== 'success_quant' ) continue
        const cyps = val( item, "rule.predict.auc_factor" ) || {}
        for ( const cyp in cyps ) {
          if ( factors[ cyp ] ) continue
          // We know genotyp
          if ( cyps[ cyp ] ) {
            const success = val( item, "rule.predict.success" ) || {}
            for ( const key in success ) {
              if ( key.indexOf( cyp.toUpperCase() ) == 0 ) {
                factors[ cyp ] = {
                  idx: Object.keys( factors ).length + 1,
                  key: cyp.toUpperCase(),
                  val: key.replace( cyp.toUpperCase(), "" )
                }
              }
            }
          }
          // We don't know genotype
          else {
            factors[ cyp ] = {
              idx: Object.keys( factors ).length + 1,
              key: cyp.toUpperCase(),
              val: ""
            }
          }
        }
      }
      // [
      //   { idx: 1, key: "CYP2C9", val: "*PM" },
      //   {idx: 2, key: "CYP2D6", val: "n.a."}
      // ]
      return Object.values( factors ).sort( ( a, b ) => a.idx - b.idx )

    },

    // -----------------------------------------
    // Transform pair of name, rule into object
    // - createSection
    // - textblock
    // -----------------------------------------
    special: function ( lang ) {

      const items = this.list( lang )
      const result = {}

      for ( const item of items ) {

        // Quick reference
        const data = result[ item.name ] = {
          pathway: {},
          inhibit: {},
          induces: {}
        }

        // It is possible that loading on rules and kinetic has not finished
        // so abort at this moment
        if ( !item.rule || !item.rule.param || !item.rule.modif ) {
          continue
        }

        item.rule.param.forEach( cur => {
          if ( /^path parent/.test( cur ) ) {
            const match = cur.match( /^path parent\s?([A-Z][A-Z0-9\-\_]+)?\s?([^\(\[]*)?/ )
            data.pathway = cytho( data.pathway, match )
            data.pathway = trans( data.pathway, match )
          }
        } )

        item.rule.modif.forEach( cur => {
          if ( /^parent inhibit/.test( cur ) ) {
            const match = cur.match( /^parent inhibit\s?([A-Z][A-Z0-9\-\_]+)?\s?([^\(\[]*)?/ )
            data.inhibit = cytho( data.inhibit, match )
            data.inhibit = trans( data.inhibit, match )
          }
          if ( /^parent induces/.test( cur ) ) {
            const match = cur.match( /^parent induces\s?([A-Z][A-Z0-9\-\_]+)?\s?([^\(\[]*)?/ )
            data.induces = cytho( data.induces, match )
            data.induces = trans( data.induces, match )
          }
        } )

      }

      return result

    },

    // -----------------------------
    // Deprecated orange level bar
    // -----------------------------
    level: function ( lang ) {

      const special = this.special( lang )
      var level = 0;

      if ( !special || !Object.keys( special ).length ) {
        return level
      }

      for ( const name in special ) {

        for ( const ct in special[ name ].inhibit ) {
          if ( special[ name ].inhibit[ ct ].includes( "strong" ) ) {
            level += 2
          } else if ( special[ name ].inhibit[ ct ].includes( "moderate" ) ) {
            level += 1
          } else if ( special[ name ].inhibit[ ct ].includes( "weak" ) ) {
            level += 0.5
          } else {
            level += 0.5
          }
        }

        for ( const ct in special[ name ].induces ) {
          if ( special[ name ].induces[ ct ].includes( "strong" ) ) {
            level += 2
          } else if ( special[ name ].induces[ ct ].includes( "moderate" ) ) {
            level += 1
          } else if ( special[ name ].induces[ ct ].includes( "weak" ) ) {
            level += 0.5
          } else {
            level += 0.5
          }
        }

      }

      return level
    }
  }
}

// ---------------------------------
// Handle pathway, inhibit, induces
// Merge into result other matches
// ---------------------------------
export function read( result, match, filter ) {
  // Catch if has not been tested
  if ( !match[ 1 ] ) return result
  // Extract
  const key = match[ 1 ].trim()
  const val = match[ 2 ] ? match[ 2 ].trim() : ""
  // Not interesting
  if ( !filter.includes( key ) ) return result
  // Enforce object
  result = result || {}
  // Return fresh object
  return immu.set( result, key, val )
}

export function cytho( result, match ) {
  const cyp = []
  cyp.push( "CYP1A2" )
  cyp.push( "CYP2B6" )
  cyp.push( "CYP2C8", "CYP2C9", "CYP2C19" )
  cyp.push( "CYP2D6" )
  cyp.push( "CYP2E1" )
  cyp.push( "CYP3A4" )

  return read( result, match, cyp )
}

export function trans( result, match ) {
  const tra = []
  tra.push( "BCRP", "MRP2", "MRP4" ) //, "MATE1", "MATE2")
  // tra.push( "OAT1", "OAT3", "OAT4" )
  tra.push( "OATP1A2", "OATP1B1", "OATP1B3", "OATP2B1", "OATP4C1" )
  tra.push( "PGP" )
  // tra.push( "OCT1", "OCT2" )
  tra.push( "UGT1A1", "UGT1A4", "UGT1A9", "UGT2B7" )

  return read( result, match, tra )
}

// ------------------------------------------------------
// Parse kinetic rules
// From view-editor/data-rule-kinetic/value
// ------------------------------------------------------

export function value( prefix, line, unit = "", debug = "" ) {

  // Check for floating and integer numbers
  const float = "[0-9]*[.]?[0-9]+"
  // Prefix can include space and chars
  const prevs = `^${prefix}`

  // ----------------------------
  // Normalize units
  // ----------------------------
  var patt1
  // If the start char is identical put the longer pattern first
  if ( unit == "L/hr" ) {
    patt1 = [ "mL/min/kg", "mL/hr/kg", "L/hr/kg", "L/hr/m2", "mL/hr", "L/hr", "mL/min", "L/min" ].join( "|" )
  } else if ( unit == "hr" ) {
    patt1 = `hr|min|days|weeks|months|seconds`
  } else if ( unit == "L" ) {
    patt1 = `mL/kg|L/kg|L`
  } else {
    patt1 = unit
  }

  // Construct pattern
  const patty = new RegExp( `${prevs} [<>]?(${float})[-]?(${float})?[ ]?(${patt1})?` )
  // Check value only if not already set to null
  const match = line.match( patty )

  if ( debug == "propranololx" ) console.log( unit, match )

  // Report if no match or not found unit
  if ( ( !match || ( unit && !match[ 3 ] ) ) && debug ) {
    console.log( "Value:", debug, "??", line )
  }

  // No match so return null
  if ( !match ) {
    return null
  }

  // -------------------------------
  // Transform match into number
  // -------------------------------
  var value

  // Found one number of range
  if ( match[ 1 ] && !match[ 2 ] ) {
    value = ( parseFloat( match[ 1 ] ) )
  }
  if ( match[ 1 ] && match[ 2 ] ) {
    value = ( parseFloat( match[ 1 ] ) + parseFloat( match[ 2 ] ) ) / 2
  }

  // -------------------------------
  // Normalize values for units
  // -------------------------------
  // Percentage
  if ( unit == "%" && match[ 3 ] == "%" ) {
    value = value / 100
  }
  // Flow
  if ( unit == "L/hr" && match[ 3 ] == "mL/hr" ) {
    value = value / 1000
  }
  if ( unit == "L/hr" && match[ 3 ] == "mL/min" ) {
    value = value * 60 / 1000
  }
  if ( unit == "L/hr" && match[ 3 ] == "L/min" ) {
    value = value * 60
  }
  if ( unit == "L/hr" && match[ 3 ] == "mL/min/kg" ) {
    value = value * 60 / 1000 * 70
  }
  if ( unit == "L/hr" && match[ 3 ] == "L/hr/kg" ) {
    value = value * 70
  }
  if ( unit == "L/hr" && match[ 3 ] == "mL/hr/kg" ) {
    value = value / 1000 * 70
  }
  if ( unit == "L/hr" && match[ 3 ] == "L/hr/m2" ) {
    value = value * 1.7
  }
  // Time
  if ( unit == "hr" && match[ 3 ] == "min" ) {
    value = value / 60
  }
  if ( unit == "hr" && match[ 3 ] == "seconds" ) {
    value = value / 3600
  }
  if ( unit == "hr" && match[ 3 ] == "days" ) {
    value = value * 24
  }
  if ( unit == "hr" && match[ 3 ] == "weeks" ) {
    value = value * 7
  }
  if ( unit == "hr" && match[ 3 ] == "months" ) {
    value = value * 24 * 30.5
  }
  // Volume
  if ( unit == "L" && match[ 3 ] == "L/kg" ) {
    value = value * 70
  }
  if ( unit == "L" && match[ 3 ] == "mL/kg" ) {
    value = value * 70 / 1000
  }

  return Number( value.toPrecision( 8 ) )

}